/**
 * Vínculo pessoa↔empresa/instituição — busca a empresa (com **cadastro rápido** se ainda não
 * existe), diz o que liga os dois e o vínculo nasce pronto; quando a empresa for aberta depois, a
 * pessoa já aparece lá.
 *
 * A **natureza** é o que o vínculo é: societário e trabalhista pedem um cargo (o cargo diz qual
 * posto); paciente, aluno, cliente e morador não têm cargo nenhum — o paciente de um residencial
 * geriátrico é paciente, ponto. O **rótulo** é livre e vira o nome da aresta no grafo
 * (`VINCULADA_A`): "Paciente desde 2019" diz mais do que a natureza sozinha.
 */
import { Briefcase, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { CamposVinculo } from '@/components/crud/CamposVinculo';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { estadoDaLista } from '@/components/ui/estado';
import {
  api,
  type EmpregoCad,
  NATUREZA_USA_CARGO,
  NATUREZAS_VINCULO,
} from '@/lib/api';
import { formatar } from '@/lib/mascaras';
import { formatarData } from '@/lib/datas';

const BUSCA_EMPRESA: ConfigBusca = {
  endpoint: '/cadastro/empresas',
  rotulo: (e) => String(e.razao_social ?? ''),
  sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
  placeholder: 'Buscar empresa por razão social ou CNPJ…',
  camposRapido: [
    { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
    { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
  ],
};

/** Como o vínculo se lê: o que o operador escreveu vence, depois o cargo, depois a natureza.
 *
 * A natureza NUNCA aparece crua: `morador` é código de banco. A tela mostra «Morador». */
function rotuloVinculo(v: EmpregoCad): string {
  if (v.rotulo) return v.rotulo;
  if (v.cargo?.nome) return v.cargo.nome;
  return NATUREZAS_VINCULO.find((n) => n.valor === v.natureza)?.rotulo ?? 'Vínculo';
}

export function PainelEmpregos({ pessoaId }: { pessoaId: string }) {
  const base = `/cadastro/pessoas/${pessoaId}/empregos`;
  const [empregos, setEmpregos] = useState<EmpregoCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [empresa, setEmpresa] = useState<ItemBusca | null>(null);
  const [natureza, setNatureza] = useState('societario');
  const [cargoId, setCargoId] = useState('');
  const [rotulo, setRotulo] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null); // id do vínculo

  async function carregar() {
    const r = await api.get<EmpregoCad[]>(base);
    setCarregando(false);
    if (r.ok) setEmpregos(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const usaCargo = NATUREZA_USA_CARGO.includes(natureza);

  function limparForm() {
    setEmpresa(null);
    setNatureza('societario');
    setCargoId('');
    setRotulo('');
    setInicio('');
    setFim('');
    setObservacoes('');
  }

  async function adicionar(): Promise<boolean> {
    if (!empresa || (usaCargo && !cargoId) || salvando) return false;
    setSalvando(true);
    const r = await api.post('/cadastro/empregos', {
      empresa_id: empresa.id,
      pessoa_id: pessoaId,
      natureza,
      cargo_id: usaCargo ? cargoId : undefined,
      rotulo: rotulo.trim() || undefined,
      data_inicio: inicio || undefined,
      data_fim: fim || undefined,
      observacoes: observacoes.trim() || undefined,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success(`Vínculo com ${String(empresa.razao_social)} criado.`);
    limparForm();
    void carregar();
    return true;
  }

  async function remover(v: EmpregoCad) {
    const r = await api.del(`/cadastro/empregos/${v.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Vínculo removido.');
    void carregar();
  }

  async function salvarEdicao(v: EmpregoCad, fim: string, obs: string) {
    const r = await api.patch(`/cadastro/empregos/${v.id}`, {
      data_fim: fim || null,
      observacoes: obs.trim() || null,
    });
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Vínculo atualizado.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="size-4 text-accent" />
          Empresas e instituições
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Sócio, funcionário, cliente, paciente… O vínculo aparece também no cadastro da empresa e
          vira aresta no grafo.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, empregos.length === 0, 'Nenhum vínculo empresarial ainda.') ?? (
          <div className="flex flex-col gap-2">
            {empregos.map((v) => {
              const chave = v.id;
              return editando === chave ? (
                <FormEdicaoEmprego
                  key={chave}
                  emprego={v}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(fim, obs) => void salvarEdicao(v, fim, obs)}
                />
              ) : (
                /* Cada vínculo é um painelzinho: faixa com fundo no topo, corpo embaixo.
                   O VÍNCULO encabeça, e não uma etiqueta discreta na borda direita — este painel
                   já é sobre empresas, e o que muda de uma linha para outra, e o que o
                   investigador lê primeiro, é O QUE liga a pessoa àquela instituição. As ações
                   moram na faixa porque é dela que são: editar/remover agem sobre o vínculo. */
                <div
                  key={chave}
                  className="overflow-hidden rounded-md border border-border"
                >
                  {/* `py-0.5`: a altura da faixa é ditada pelos botões, não pelo respiro. Mais
                      padding só empurraria o conteúdo do vínculo seguinte para fora da tela. */}
                  <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-2 px-3 py-0.5">
                    <span className="truncate text-[14px] font-medium text-text">
                      {rotuloVinculo(v)}
                    </span>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button variant="fantasma" size="sm" onClick={() => setEditando(chave)} aria-label="Editar">
                        <Pencil />
                      </Button>
                      <Button
                        variant="fantasma"
                        size="sm"
                        className="text-danger hover:text-danger"
                        onClick={() => void remover(v)}
                        aria-label="Remover"
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-col px-3 py-2">
                    <span className="truncate tipo-corpo text-text">{v.empresa.razao_social}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      desde {formatarData(v.data_inicio)}
                      {v.data_fim ? ` · até ${formatarData(v.data_fim)}` : ' · atual'}
                      {v.observacoes && ` · ${v.observacoes}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo vínculo empresarial"
          descricao="Busque a empresa (ou cadastre-a na hora) e escolha o cargo. Aparece também no cadastro da empresa."
          rotuloAbrir="Adicionar vínculo"
          podeSalvar={!!empresa && (!usaCargo || !!cargoId)}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Empresa</span>
            <ComboBuscaEntidade config={BUSCA_EMPRESA} onSelecionar={setEmpresa} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">O que liga os dois</span>
            <select
              value={natureza}
              onChange={(e) => setNatureza(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              {NATUREZAS_VINCULO.map((n) => (
                <option key={n.valor} value={n.valor}>
                  {n.rotulo}
                </option>
              ))}
            </select>
          </label>
          {usaCargo && (
            <label className="flex flex-col gap-2">
              <span className="tipo-rotulo">Cargo</span>
              <ComboReferencia
                id="emprego-cargo"
                valor={cargoId}
                onChange={setCargoId}
                endpoint="/cadastro/cargos"
                permitirCriar
              />
            </label>
          )}
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Rótulo (opcional)</span>
            <Input
              value={rotulo}
              onChange={(e) => setRotulo(e.target.value)}
              placeholder="Como este vínculo aparece no grafo — ex.: Paciente desde 2019"
            />
          </label>
          <CamposVinculo
            inicio={inicio}
            fim={fim}
            observacoes={observacoes}
            onInicio={setInicio}
            onFim={setFim}
            onObservacoes={setObservacoes}
          />
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}

/** Edição in-place do emprego: fim e observação (empresa/cargo/início são a identidade). */
function FormEdicaoEmprego({
  emprego,
  onSalvar,
  onCancelar,
}: {
  emprego: EmpregoCad;
  onSalvar: (fim: string, obs: string) => void;
  onCancelar: () => void;
}) {
  const [fim, setFim] = useState(emprego.data_fim ?? '');
  const [obs, setObs] = useState(emprego.observacoes ?? '');
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">
        {rotuloVinculo(emprego)} — {emprego.empresa.razao_social}
      </span>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Até</span>
          <input
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
          />
        </label>
        <label className="flex flex-1 flex-col gap-2">
          <span className="tipo-rotulo">Observações</span>
          <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="…" />
        </label>
        <Button type="button" variant="secundario" onClick={() => onSalvar(fim, obs)}>
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}
